import { useEffect, useState } from "react";
import { type DeviceInfo } from "shared/types/DeviceInfo";
import { getDeviceInfo } from "hub/src/utils/getDeviceInfo";
import { PARENT_VIEWPORT_CHANGE_EVENT } from "hub/src/utils/iframe/parentViewport";

type UseDeviceInfoProps = {
  /**
   * Re-read the screen size and orientation when the window resizes or the
   * device rotates. Off by default: the platform flags never change, and most
   * callers only need those, so they skip the re-renders a resize would cause.
   */
  watchScreen?: boolean;
};

const isSameScreen = (a: DeviceInfo, b: DeviceInfo): boolean =>
  a.width === b.width &&
  a.height === b.height &&
  a.orientation === b.orientation;

const useDeviceInfo = ({
  watchScreen = false,
}: UseDeviceInfoProps = {}): DeviceInfo => {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(getDeviceInfo);

  useEffect(() => {
    setDeviceInfo(getDeviceInfo());
  }, []);

  useEffect(() => {
    if (!watchScreen) {
      return;
    }

    // Resize fires many times per drag; reading once per frame is enough.
    let frame: number | undefined;
    const handleResize = () => {
      if (frame !== undefined) {
        return;
      }
      frame = window.requestAnimationFrame(() => {
        frame = undefined;
        const next = getDeviceInfo();
        setDeviceInfo((previous) =>
          isSameScreen(previous, next) ? previous : next,
        );
      });
    };

    // Inside the SSO iframe the parent stretches the iframe to fit its content,
    // which fires "resize" here every time the page grows. Height and
    // orientation then come from the parent window, so a stretch leaves the
    // measurements equal and `isSameScreen` skips the re-render; the parent's
    // own resizes arrive as PARENT_VIEWPORT_CHANGE_EVENT.
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    window.addEventListener(PARENT_VIEWPORT_CHANGE_EVENT, handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
      window.removeEventListener(PARENT_VIEWPORT_CHANGE_EVENT, handleResize);
      if (frame !== undefined) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [watchScreen]);

  return deviceInfo;
};

export default useDeviceInfo;
